import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loader from "../../common/Loader";
import {
  Build,
  BusinessCenter,
  ChevronLeft,
  ChevronRight,
  Close,
  Comment,
  Edit,
  MoreVert,
  Room
} from "@mui/icons-material";
import { BIBlue, BIGrey, BIRed } from "../../assets/helpers";
import { useDispatch, useSelector } from "react-redux";
import { English } from "../../constant/EnglishFallback";
import mapMarkerYellow from "../../assets/images/marker_yellow.png";
import { GET_POST_COMMENTS, POSTS_USER_SETTINGS, SAVE_POST_COMMENT } from "../../constant/Apipath";
import ImageIcon from "@mui/icons-material/Image";
import SendIcon from "@mui/icons-material/Send";
import { Getrequestcall } from "../../request/Getrequest";
import { Postrequestcall } from "../../request/Postrequest";
import BIDMarker from "../../assets/images/marker_yellow.png";
import { getFormattedDate } from "../../common/Helper";
import { useNavigate } from "react-router-dom";
import defaultProjectImage from "../../assets/images/project_profile.jpg";
import { Carousel } from "react-responsive-carousel";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import Pages from "../../constant/ReportModel";
import { setShowReport } from "../../redux/actions/actions";

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".m4v"];
  return videoExtensions.some((ext) => url.toLowerCase().includes(ext));
};

const VideoComponent = memo(({ postItem }) => {
  const containerRef = useRef(null);
  const videoNode = useRef(null);
  const playerRef = useRef(null);
  const [playerInstance, setPlayerInstance] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitializedRef = useRef(false);

  // Use postItem prop instead of internal state
  const pictureList = useMemo(
    () => [{ pictureUrl: postItem?.pictureList?.[0]?.pictureUrl }],
    [postItem?.pictureList]
  );

  const [displayMessage] = useState("Sample Video");
  const [id] = useState(Math.random()); // Use random ID to avoid conflicts
  const [formatTypeId] = useState(7);
  const [activeIndex, setActiveIndex] = useState(null);

  const playerConfig = useMemo(
    () => ({
      controls: true,
      autoplay: false,
      muted: true,
      preload: "metadata",
      fluid: false,
      responsive: false,
      width: "100%",
      height: 350
    }),
    []
  );

  const source = useMemo(() => {
    const pictureUrl = pictureList[0]?.pictureUrl;
    return {
      title: displayMessage || "",
      hls: pictureUrl?.endsWith(".m3u8") ? pictureUrl : undefined,
      dash: pictureUrl?.endsWith(".mpd") ? pictureUrl : undefined,
      progressive: pictureUrl?.endsWith(".mp4")
        ? [{ url: pictureUrl, type: "video/mp4" }]
        : undefined
    };
  }, [pictureList, displayMessage]);

  const getVideoSource = useCallback(() => {
    if (!source) return null;

    if (source.hls) {
      return { src: source.hls, type: "application/x-mpegURL" };
    } else if (source.dash) {
      return { src: source.dash, type: "application/dash+xml" };
    } else if (source.progressive) {
      return { src: source.progressive[0].url, type: "video/mp4" };
    }
    return null;
  }, [source]);

  const initializePlayer = useCallback(() => {
    if (!videoNode.current || playerRef.current || isInitializedRef.current) return;

    const videoSource = getVideoSource();
    if (!videoSource) return;

    isInitializedRef.current = true;
    setIsLoading(true);

    playerRef.current = videojs(
      videoNode.current,
      {
        ...playerConfig,
        sources: [videoSource]
      },
      () => {
        setPlayerReady(true);
        setPlayerInstance(playerRef.current);
        setIsLoading(false);
        setError(null);
        console.log("Video player is ready");
      }
    );

    // Set up event listeners for player ready state
    playerRef.current.on("ready", () => {
      setPlayerReady(true);
      setIsLoading(false);
      setError(null);
    });

    playerRef.current.on("sourceloaded", () => {
      setIsLoading(false);
    });

    playerRef.current.on("play", () => {
      setIsLoading(false);
    });

    playerRef.current.on("error", (errorEvent) => {
      setError(errorEvent);
      setIsLoading(false);
      console.error("handlePlayerRef-> Error In Player: ", errorEvent);
    });

    // Add keyboard controls
    addKeyboardControls();
  }, [getVideoSource, playerConfig]);

  const addKeyboardControls = useCallback(() => {
    const handlePlayPause = () => {
      if (!playerRef.current) return;
      playerRef.current.paused() ? playerRef.current.play() : playerRef.current.pause();
    };

    const handleKeyboardEvent = (event) => {
      if (event.key === "Enter" || event.key === "GamepadA") {
        handlePlayPause();
      }
    };

    window.addEventListener("keydown", handleKeyboardEvent);
    playerRef.current.keyboardCleanup = () => {
      window.removeEventListener("keydown", handleKeyboardEvent);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setActiveIndex(id);
        } else {
          setActiveIndex(null);
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [id]);

  useEffect(() => {
    if (!playerReady || !playerInstance) return;

    if (activeIndex === id) {
      playerInstance
        .play()
        .catch((err) => console.error("useEffect-> Error As Autoplay Failed: ", err));
    } else {
      playerInstance.pause();
    }
  }, [activeIndex, id, playerInstance, playerReady]);

  // Initialize player
  useEffect(() => {
    if (videoNode.current && source && Object.values(source).some(Boolean)) {
      const timer = setTimeout(initializePlayer, 100);
      return () => clearTimeout(timer);
    }
  }, [initializePlayer, source]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.keyboardCleanup?.();
        playerRef.current.dispose();
        playerRef.current = null;
        isInitializedRef.current = false;
        setPlayerReady(false);
        setPlayerInstance(null);
      }
    };
  }, []);

  // Validate source
  if (
    !(formatTypeId === 7 && pictureList?.length) ||
    (!source.hls && !source.dash && !source.progressive)
  ) {
    return null;
  }

  const SimpleLoader = () => (
    <div
      style={{
        position: "absolute",
        top: "43.5%",
        left: "45%",
        zIndex: 10,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: "16px 24px",
        borderRadius: "50%",
        color: "white",
        fontSize: "14px",
        fontWeight: "500"
      }}>
      Loading...
    </div>
  );

  const ErrorMessage = () => (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        zIndex: 10,
        backgroundColor: "rgba(220, 53, 69, 0.9)",
        padding: "20px",
        borderRadius: "8px",
        color: "white",
        textAlign: "center"
      }}>
      <div style={{ fontSize: "24px" }}>⚠️</div>
      <div>
        <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
          Failed to load video
        </div>
        <div style={{ fontSize: "14px", opacity: 0.9 }}>Please try refreshing the page</div>
      </div>
    </div>
  );

  return (
    <Stack
      ref={containerRef}
      style={{
        position: "relative",
        minHeight: "350px",
        backgroundColor: "#000",
        borderRadius: "8px",
        overflow: "hidden"
      }}>
      {/* Simple loading overlay */}
      {isLoading && <SimpleLoader />}

      {/* Error overlay */}
      {error && <ErrorMessage />}

      {/* Click prevention overlay while loading */}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5,
            cursor: "not-allowed"
          }}
          onClick={(e) => e.preventDefault()}
        />
      )}

      {/* Video Player */}
      <div style={{ borderRadius: "10px", overflow: "hidden" }}>
        <div
          data-vjs-player
          style={{
            width: "100%",
            height: "350px",
            minHeight: "350px",
            backgroundColor: "#000"
          }}>
          <video
            ref={videoNode}
            className="video-js vjs-default-skin vjs-big-play-centered"
            playsInline
            style={{
              width: "100%",
              height: "350px",
              minHeight: "350px",
              display: "block"
            }}
          />
        </div>
      </div>
    </Stack>
  );
});

export default function PostComment({ postItem, setSelectPost, open, onClose, setPosts, posts }) {
  const { multiLanguageData, selectedLanguage, loginData, reportSuccess, reportData } = useSelector(
    (state) => state?.main
  );

  const fileInputRef = useRef();
  const navigate = useNavigate();

  const [loader, setLoader] = useState(false);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [uploadedImage, setUploadedImage] = useState({
    preview: null,
    raw: null
  });
  const [comment, setComment] = useState("");
  const [sendComment, setSendComment] = useState(false);
  const [comments, setComments] = useState([]);

  const dispatch = useDispatch();

  const {
    TEXT_REPORT = English?.Home?.TEXT_REPORT,
    MORE_MENU_ON_NOTIFICATIONS = English?.Home?.MORE_MENU_ON_NOTIFICATIONS,
    MORE_MENU_OFF_NOTIFICATIONS = English?.Home?.MORE_MENU_OFF_NOTIFICATIONS,
    MODAL_TEXT_COMMENT_PLACEHOLDER = English?.Home?.MODAL_TEXT_COMMENT_PLACEHOLDER
  } = multiLanguageData?.Home || {};

  const postIconStyle = {
    fontSize: "50px",
    color: BIBlue
  };

  useEffect(() => {
    if (open === true && postItem?.nrComments > 0 && reportSuccess !== true) {
      fetchPostComments();
      // Decrease the post comment count after report success
      if (reportData?.AppScreenId === Pages?.postComment?.id) {
        decreasePostCommentAfterReport();
      }
    }
  }, [open, reportSuccess]);

  const decreasePostCommentAfterReport = () => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.id === postItem?.id
          ? { ...post, nrComments: Math.max(0, (post.nrComments || 0) - 1) }
          : post
      )
    );
    let reportObj = {
      showReport: false,
      recordId: 0,
      name: "",
      AppScreenId: 0
    };
    dispatch(setShowReport(reportObj));
  };

  const fetchPostComments = async () => {
    try {
      setLoader(true);
      let pageNr = 1;
      let nrOfRecPerPage = 50;
      const getPostRes = await Getrequestcall(
        `${GET_POST_COMMENTS}/${loginData?.userInformation?.UserId}/${postItem?.id}/${pageNr}/${nrOfRecPerPage}/${postItem?.appId}/${selectedLanguage || "en"}`,
        loginData?.accessToken
      );
      if (getPostRes?.status === 200 && getPostRes?.data?.message === "OK") {
        setComments(getPostRes?.data?.data || []);
      }
    } catch (error) {
    } finally {
      setLoader(false);
    }
  };

  const getPostIcon = (postIcon) => {
    switch (postIcon) {
      case "Briefcase":
        return <BusinessCenter sx={postIconStyle} />;
      case "Map Pin":
        return <Room sx={postIconStyle} />;
      case "Wrench":
        return <Build sx={postIconStyle} />;
      case "Pencil":
        return <Edit sx={postIconStyle} />;
      default:
        return;
    }
  };

  const handleClose = () => {
    setComments([]);
    onClose();
  };

  const handleMutePostMenu = async (id, currentMuteStatus) => {
    try {
      //return;
      const mutePostObj = {
        UserId: Number(loginData?.userInformation?.UserId),
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [
          {
            PostId: Number(id),
            UserId: Number(loginData?.userInformation?.UserId),
            IsMute: !currentMuteStatus
          }
        ]
      };
      let mutePostRes = await Postrequestcall(
        POSTS_USER_SETTINGS,
        mutePostObj,
        loginData?.accessToken
      );
      if (mutePostRes?.status === 200) {
        // Update the post mute status in state
        const updatedPosts = posts.map((post) => {
          if (post.id === id) {
            return { ...post, isMute: !currentMuteStatus };
          }
          return post;
        });
        setSelectPost((prev) => ({
          ...prev,
          isMute: !currentMuteStatus
        }));
        setPosts(updatedPosts);
      }
      handleMenuClose();
    } catch (error) {
      console.error("handleMutePost-> Error: ", error);
      handleMenuClose();
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReportPost = (postItem) => {
    try {
      handleMenuClose();
      const reportObj = {
        showReport: true,
        name: Pages?.post?.name,
        AppScreenId: Pages?.post?.id,
        recordId: postItem?.id || 0,
        appName: postItem?.appName || ""
      };
      dispatch(setShowReport(reportObj));
    } catch (error) {
      console.error("handleReportPost-> Error: ", error);
      handleMenuClose();
    }
  };

  const handleReportComment = (comment) => {
    // Implement the logic for reporting a post here
    try {
      handleMenuClose();
      const reportObj = {
        showReport: true,
        name: Pages?.post?.name,
        AppScreenId: Pages?.postComment?.id,
        recordId: comment?.id || 0
      };
      dispatch(setShowReport(reportObj));
    } catch (error) {
      console.error("handleReportPost-> Error: ", error);
      handleMenuClose();
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current.click();
  };

  const onCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedImage({
        preview: URL.createObjectURL(file),
        raw: file
      });
    }
    event.target.value = "";
  };

  const handleSaveComment = async () => {
    try {
      // Validate comment is not empty
      if ((!comment || comment.trim() === "") && !uploadedImage?.raw) {
        setComment("");
        alert("Comment cannot be empty");
        return;
      }

      setSendComment(true);
      let data = new FormData();
      data.append("Id", "0");
      data.append("PostId", postItem?.id);
      data.append("UserId", loginData?.userInformation?.UserId);
      data.append("Comment", comment.trim());
      data.append("ModifiedBy", loginData?.userInformation?.UserId);
      data.append("AppId", postItem?.appId);
      data.append("LanguageCode", selectedLanguage || "en");
      uploadedImage.raw && data.append("Pictures", uploadedImage.raw);
      setComment("");
      const saveCommentRes = await Postrequestcall(
        SAVE_POST_COMMENT,
        data,
        loginData?.accessToken,
        true
      );
      if (saveCommentRes?.status === 201 && saveCommentRes?.data?.message === "OK") {
        setSendComment(false);
        fetchPostComments();
        // Update comment count in the post
        const newCommentCount = postItem?.nrComments ? postItem?.nrComments + 1 : 1;
        postItem.nrComments = newCommentCount;
        // Update the post in the parent component's state
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postItem.id ? { ...post, nrComments: newCommentCount } : post
          )
        );
      }
    } catch (error) {
    } finally {
      if (uploadedImage.preview) {
        URL.revokeObjectURL(uploadedImage.preview);
      }
      setUploadedImage({ preview: null, raw: null });
    }
  };

  const commentDate = (age) => {
    return age
      ? age?.includes("min") ||
        age?.includes("hours") ||
        age?.includes("days") ||
        age?.includes("hour") ||
        age?.includes("day")
        ? age
        : getFormattedDate(age)
      : "";
  };

  const CommentList = ({ comment }) => {
    const [commentAnchorEl, setCommentAnchorEl] = React.useState(null);
    const commentMenuOpen = Boolean(commentAnchorEl);

    const handleCommentMenuOpen = (event) => {
      setCommentAnchorEl(event.currentTarget);
    };

    const handleCommentMenuClose = () => {
      setCommentAnchorEl(null);
    };

    const handleProfileClick = () => {
      if (loginData?.userInformation?.UserId !== comment?.authorUserId && postItem?.appId !== 19)
        navigate(`/${comment?.authorUserId}`);
    };

    return (
      <Card
        key={comment?.id}
        sx={{ mb: 1, pb: 2, bgcolor: "#ffff", boxShadow: "none", border: "1px solid #c3bebe" }}>
        <CardHeader
          classes={{ avatar: "post-comment-avatar" }}
          style={{ padding: "10px" }}
          avatar={
            <Avatar
              src={postItem?.appId === 19 ? BIDMarker : comment?.authorPictureUrl}
              alt="author-pic"
              sx={{
                width: postItem?.appId === 19 ? "100%" : 40,
                height: postItem?.appId === 19 ? 20 : 40
              }}
            />
          }
          action={
            <>
              <IconButton aria-label="settings" onClick={handleCommentMenuOpen}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={commentAnchorEl}
                open={commentMenuOpen}
                onClose={handleCommentMenuClose}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                slotProps={{
                  paper: {
                    style: {
                      maxHeight: 200
                    }
                  }
                }}>
                {/* Report Post option */}
                <MenuItem
                  onClick={() => handleReportComment(comment)}
                  style={{ color: BIRed }}
                  dense>
                  {TEXT_REPORT}
                </MenuItem>
              </Menu>
            </>
          }
          title={
            <Typography
              variant="body2"
              fontWeight="bold"
              style={{
                cursor:
                  loginData?.userInformation?.UserId !== comment?.authorUserId &&
                  postItem?.appId !== 19 &&
                  "pointer"
              }}
              onClick={handleProfileClick}>
              {`${comment?.authorFirstName} ${comment?.authorLastName} ${
                comment?.tradeName && postItem?.appId !== 19 ? ` (${comment?.tradeName})` : ""
              }`}
            </Typography>
          }
          subheader={postItem?.appId === 19 ? comment?.tradeName || "" : commentDate(comment?.age)}
        />
        <CardContent style={{ padding: "0px 7px" }}>
          <Typography variant="body2" fontSize={"14px"} sx={{ color: BIGrey }}>
            {comment?.translatedMessage || comment?.comment}
          </Typography>
        </CardContent>
        {postItem?.appId === 19 && (
          <>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent={"center"}>
              <IconButton>
                <Comment />
              </IconButton>
              <Typography variant="body2">{comment?.nrComments}</Typography>
            </Stack>
          </>
        )}
        {comment?.pictureUrl && (
          <CardMedia
            component="img"
            height="200"
            image={comment?.pictureUrl}
            alt="comment-pic"
            style={{ objectFit: "contain" }}
          />
        )}
      </Card>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={"user-modal-responsive"}>
        <Card elevation={0} mb={0} sx={{ overflow: "visible" }}>
          <CardHeader
            classes={{ avatar: "post-header-avatar" }}
            sx={{ padding: "0px" }}
            avatar={
              <div>
                <Avatar
                  src={postItem?.postedByPictureUrl}
                  alt=""
                />
              </div>
            }
            action={
              <>
                {postItem?.appId !== 19 && (
                  <IconButton onClick={handleMenuClick} className="morevert-icon">
                    <MoreVert />
                  </IconButton>
                )}
                <IconButton onClick={handleClose} className="close-btn">
                  <Close />
                </IconButton>
              </>
            }
            title={
              <Typography variant="body2" sx={{ fontSize: "14px" }} fontWeight="bold">
                {postItem?.postedByUserName ||
                  postItem?.postedByFirstName + " " + postItem?.postedByLastName ||
                  ""}{" "}
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="textSecondary">
                {postItem?.tradeName || "" + " • " + commentDate(postItem?.postedDate)}
              </Typography>
            }
          />
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}>
            {/* Show Mute/Unmute option if the post is by the current user */}
            {Number(postItem?.postedByUserId) === Number(loginData?.userInformation?.UserId) && (
              <MenuItem
                onClick={() => {
                  handleMutePostMenu(postItem?.id, postItem?.isMute);
                }}
                style={{ color: BIBlue }}
                dense>
                {postItem?.isMute ? MORE_MENU_ON_NOTIFICATIONS : MORE_MENU_OFF_NOTIFICATIONS}
              </MenuItem>
            )}
            <MenuItem style={{ color: BIRed }} dense onClick={() => handleReportPost(postItem)}>
              {TEXT_REPORT}
            </MenuItem>
          </Menu>
          <CardContent sx={{ padding: "12px 0px", paddingBottom: "0px !important" }}>
            <Typography align="center">{getPostIcon(postItem?.icon)}</Typography>
            <Typography fontSize={"14px"} marginBottom={"10px"} variant="body2">
              {postItem?.translatedMessage ||
                (postItem?.message !== "<<<picture>>>" ? postItem?.message : "")}{" "}
            </Typography>
            {
              // For Site details
              postItem?.formatTypeId === 5 && (
                <Box
                  sx={{
                    width: "100%",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                    marginBottom: 2
                  }}>
                  <img
                    src={postItem?.pictureList?.[0]?.pictureUrl || defaultProjectImage}
                    alt={"Site"}
                    style={{
                      width: "100%",
                      height: "100%",
                      maxHeight: "400px",
                      objectFit: "contain",
                      objectPosition: "center",
                      display: "block",
                      backgroundColor: "#fff",
                      transition: "opacity 0.3s ease"
                    }}
                  />
                </Box>
              )
            }
            {postItem?.formatTypeId === 7 ? (
              <Box style={{ width: "100%", height: "350px" }}>
                <VideoComponent postItem={postItem} />
              </Box>
            ) : (
              postItem?.pictureList &&
              postItem?.pictureList.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    borderRadius: "12px",
                    overflow: "hidden",
                    "& .carousel .slide": { backgroundColor: "#1a1a1a" },
                    "& .carousel .carousel-status": {
                      fontSize: "12px",
                      fontWeight: 600,
                      background: "rgba(0,0,0,0.55)",
                      borderRadius: "4px",
                      padding: "2px 8px",
                      top: 8,
                      right: 8,
                      textShadow: "none"
                    },
                    "& .carousel .control-dots": { marginBottom: 0 },
                    "& .carousel .control-dots .dot": {
                      boxShadow: "none",
                      background: "#fff",
                      opacity: 0.5,
                      width: 7,
                      height: 7
                    },
                    "& .carousel .control-dots .dot.selected": { opacity: 1 }
                  }}>
                  <Carousel
                    showThumbs={false}
                    showStatus={postItem.pictureList.length > 1}
                    statusFormatter={(c, t) => `${c} / ${t}`}
                    showIndicators={postItem.pictureList.length > 1}
                    infiniteLoop={postItem.pictureList.length > 1}
                    useKeyboardArrows
                    renderArrowPrev={(onClickHandler, hasPrev) =>
                      hasPrev && (
                        <IconButton
                          onClick={onClickHandler}
                          sx={{
                            position: "absolute",
                            left: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 2,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            p: 0.5,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" }
                          }}>
                          <ChevronLeft fontSize="small" />
                        </IconButton>
                      )
                    }
                    renderArrowNext={(onClickHandler, hasNext) =>
                      hasNext && (
                        <IconButton
                          onClick={onClickHandler}
                          sx={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 2,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            p: 0.5,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" }
                          }}>
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      )
                    }>
                    {postItem.pictureList.map((picture, index) => (
                      <div
                        key={picture?.id ? `${picture.id}-${index}` : index}
                        style={{
                          height: "320px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#1a1a1a"
                        }}>
                        {isVideoUrl(picture?.pictureUrl) ? (
                          <video
                            controls
                            style={{
                              maxWidth: "100%",
                              maxHeight: "320px",
                              objectFit: "contain"
                            }}
                            src={picture?.pictureUrl}
                            preload="metadata"
                          />
                        ) : (
                          <img
                            src={picture?.pictureUrl}
                            alt={`post-media-${index + 1}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "320px",
                              objectFit: "contain",
                              cursor: "pointer",
                              display: "block"
                            }}
                            onClick={() => {
                              setPictureViewerOpen(true);
                              setPictureViewIndex(index);
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </Carousel>
                </Box>
              )
            )}
          </CardContent>
        </Card>
        {/* Comments list */}
        <div style={{ maxHeight: "210px", marginTop: "15px", overflowY: "auto" }}>
          {loader ? (
            <Stack alignItems={"center"} justifyContent={"center"} height={"200px"}>
              <Loader />
            </Stack>
          ) : (
            comments.map((comment, index) => <CommentList key={index} comment={comment} />)
          )}
        </div>
        {/* Uploaded Image Preview */}
        {uploadedImage.preview && (
          <Box>
            <Box
              sx={{
                position: "relative",
                width: "100px",
                height: "100px",
                display: "inline-block"
              }}>
              <img
                src={uploadedImage.preview}
                alt="preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0"
                }}
              />
              <IconButton
                onClick={() => {
                  URL.revokeObjectURL(uploadedImage.preview);
                  setUploadedImage({ preview: null, raw: null });
                }}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  bgcolor: "white",
                  boxShadow: 2,
                  width: 24,
                  height: 24,
                  "&:hover": {
                    bgcolor: "#f5f5f5"
                  }
                }}
                size="small">
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        )}
        <Divider style={{ marginTop: "15px" }} />
        <Box
          className="post-comment"
          style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
          <Avatar src={loginData?.pictureUrl} alt="profile-pic" />
          <TextField
            variant="outlined"
            placeholder={MODAL_TEXT_COMMENT_PLACEHOLDER}
            size="small"
            sx={{ flex: 1, mx: 2 }}
            value={comment}
            onChange={onCommentChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton sx={{ color: BIBlue }} onClick={handleImageButtonClick}>
                    <ImageIcon />
                  </IconButton>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </InputAdornment>
              )
            }}
          />

          <IconButton
            disabled={!comment && !uploadedImage?.preview}
            sx={{
              color: !comment && !uploadedImage?.preview ? BIGrey : BIBlue
            }}
            onClick={handleSaveComment}>
            {sendComment ? <Loader /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
}
